import React from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { REVIEW_SOURCES, getReviewDisplayText, type ReviewSource } from '@/constants/reviewConfig';
import GoogleLogo from '@/assets/brands/google-logo.svg';
import HousecallProLogo from '@/assets/brands/housecall-pro-logo.svg';

/**
 * Star rating display with partial fill support
 */
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  
  for (let i = 0; i < 5; i++) {
    const fillPercentage = Math.min(Math.max(rating - i, 0), 1) * 100;
    stars.push(
      <div key={i} className="relative">
        <Star className="w-4 h-4 text-gray-400" fill="currentColor" />
        <div 
          className="absolute inset-0 overflow-hidden" 
          style={{ width: `${fillPercentage}%` }}
        >
          <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
        </div>
      </div>
    );
  }
  
  return <div className="flex gap-0.5">{stars}</div>;
};

/**
 * Google color accent dots
 */
const GoogleColorAccent = () => (
  <div className="flex gap-0.5 mt-1" aria-hidden="true">
    <span className="w-1.5 h-1.5 rounded-full bg-[#4285F4]" />
    <span className="w-1.5 h-1.5 rounded-full bg-[#EA4335]" />
    <span className="w-1.5 h-1.5 rounded-full bg-[#FBBC05]" />
    <span className="w-1.5 h-1.5 rounded-full bg-[#34A853]" />
  </div>
);

/**
 * Individual review source card with real branding
 */
const ReviewCard = ({ source }: { source: ReviewSource }) => {
  const displayInfo = getReviewDisplayText(source);
  const isGoogle = source.platform === 'google';
  
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-start gap-4 bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-xl p-4 md:p-5 transition-all duration-200 border border-white/10 hover:border-white/20"
      aria-label={`View ${isGoogle ? 'Google' : 'Housecall Pro'} reviews - ${displayInfo.primaryText}${displayInfo.secondaryText ? `, ${displayInfo.secondaryText}` : ''}`}
    >
      {/* Brand Logo */}
      <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white flex items-center justify-center shadow-sm">
        <img
          src={isGoogle ? GoogleLogo : HousecallProLogo}
          alt=""
          aria-hidden="true"
          width={32}
          height={32}
          loading="lazy"
          decoding="async"
          className="w-7 h-7 md:w-8 md:h-8 object-contain"
        />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Brand Name Header */}
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-white font-bold text-base md:text-lg">
            {isGoogle ? 'Google' : 'Housecall Pro'}
          </h3>
          <ExternalLink 
            className="w-4 h-4 text-white/80 group-hover:text-white transition-colors flex-shrink-0" 
            aria-hidden="true" 
          />
        </div>
        
        {/* Google-specific color accent */}
        {isGoogle && <GoogleColorAccent />}
        
        {/* Rating and Review Info */}
        <div className="mt-2">
          {source.rating && (
            <div className="flex items-center gap-2 mb-1">
              <StarRating rating={source.rating} />
              <span className="text-white font-semibold text-sm">
                {source.rating.toFixed(1)}
              </span>
            </div>
          )}
          
          <p className="text-white/80 text-sm">
            {displayInfo.primaryText}
          </p>
          
          {displayInfo.secondaryText && (
            <p className="text-white/60 text-xs mt-0.5">
              {displayInfo.secondaryText}
            </p>
          )}
        </div>
        
        {/* Read Reviews Link */}
        <span className="inline-flex items-center gap-1 text-orange text-sm font-medium mt-3 group-hover:underline">
          Read reviews
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </div>
    </a>
  );
};

/**
 * TrustedReviews - Hero trust module with real brand logos
 * 
 * Features:
 * - CSS Grid layout with guaranteed spacing (never overlaps)
 * - Real Google "G" logo and Housecall Pro logo
 * - Safe wording strategy for low review counts
 * - Fully responsive: stacked on mobile, 2-col on tablet+
 */
const TrustedReviews = () => {
  // Filter out sources with no data
  const validSources = REVIEW_SOURCES.filter(
    source => source.rating !== null || source.reviewCount !== null
  );
  
  if (validSources.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-3">
      {validSources.map((source) => (
        <ReviewCard key={source.platform} source={source} />
      ))}
    </div>
  );
};

export default TrustedReviews;
